/**
 * Seam Crossing Rush — wave (level) data.
 *
 * Each wave fixes a witness component palette, the request templates whose
 * required witness tuples the player must assemble, the spawn cadence, and the
 * quota of crossings to construct. Difficulty ramps across waves: larger tuples,
 * faster cadence, less patience.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Wave, WitnessComponent } from "../algorithms/seamCrossingRush.js";

const PALETTE: readonly WitnessComponent[] = [
  {
    id: "origin",
    label: "DATA.ORIGIN",
    glyph: "◉",
    summary: "Where the agent's authority is rooted.",
  },
  {
    id: "capability",
    label: "CAP.GRANT",
    glyph: "⊢",
    summary: "The capability token authorizing the action.",
  },
  {
    id: "freshness",
    label: "TIME.FRESH",
    glyph: "↻",
    summary: "Proof the witness set is current, not replayed.",
  },
  {
    id: "scope",
    label: "SCOPE.BIND",
    glyph: "⊞",
    summary: "Binds the crossing to a declared resource scope.",
  },
  {
    id: "attest",
    label: "ATTEST.SIG",
    glyph: "✓",
    summary: "Signed attestation of the agent's identity.",
  },
];

export const CROSSING_WAVES: readonly [Wave, ...Wave[]] = [
  {
    id: "wave-tool",
    title: "Wave 1 · Tool seams",
    brief:
      "Tool-call crossings need a small witness tuple. Assemble each agent's tuple and dispatch it before it reaches the seam — an incomplete tuple has no crossing arrow.",
    palette: PALETTE,
    spawnEveryTicks: 5,
    quota: 6,
    templates: [
      {
        id: "t1-tool",
        title: "R1 tool call",
        required: ["origin", "capability"],
        patienceTicks: 11,
      },
      {
        id: "t1-fresh-tool",
        title: "R1 tool call · fresh",
        required: ["origin", "capability", "freshness"],
        patienceTicks: 12,
      },
    ],
  },
  {
    id: "wave-external",
    title: "Wave 2 · External seams",
    brief:
      "External crossings demand scope and a signed attestation. Larger tuples arrive faster — keep the honest flow moving by constructing each one in time.",
    palette: PALETTE,
    spawnEveryTicks: 4,
    quota: 7,
    templates: [
      {
        id: "t2-subagent",
        title: "R2 sub-agent spawn",
        required: ["origin", "capability", "freshness"],
        patienceTicks: 11,
      },
      {
        id: "t2-external",
        title: "R3 external call",
        required: ["origin", "capability", "scope", "attest"],
        patienceTicks: 12,
      },
    ],
  },
  {
    id: "wave-persistent",
    title: "Wave 3 · Persistent seams",
    brief:
      "Persistent writes require the full witness tuple. The cadence is brisk and patience is short — a single missing component leaves the crossing unconstructable.",
    palette: PALETTE,
    spawnEveryTicks: 4,
    quota: 8,
    templates: [
      {
        id: "t3-external",
        title: "R3 external call",
        required: ["origin", "capability", "scope", "attest"],
        patienceTicks: 11,
      },
      {
        id: "t3-persistent",
        title: "R4 persistent write",
        required: ["origin", "capability", "freshness", "scope", "attest"],
        patienceTicks: 13,
      },
    ],
  },
];
